import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { DollarSign, Users, AlertTriangle, CheckCircle, Edit, RefreshCw, Calendar } from 'lucide-react';

export default function YTDManagement() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [taxYear, setTaxYear] = useState(2026);
  const [initStatus, setInitStatus] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  
  // Dialog states
  const [initDialogOpen, setInitDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [initForm, setInitForm] = useState({
    employee_id: '',
    ytd_gross: 0,
    ytd_federal_tax: 0,
    ytd_provincial_tax: 0,
    ytd_cpp: 0,
    ytd_cpp2: 0,
    ytd_ei: 0,
    ytd_net_pay: 0,
    notes: ''
  });
  
  const [adjustForm, setAdjustForm] = useState({
    ytd_gross: '',
    ytd_federal_tax: '',
    ytd_provincial_tax: '',
    ytd_cpp: '',
    ytd_cpp2: '',
    ytd_ei: '',
    ytd_net_pay: '',
    adjustment_reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [taxYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, ledgerRes] = await Promise.all([
        api.get(`/payroll-ytd/check-initialization?tax_year=${taxYear}`),
        api.get(`/payroll-ytd/ledger?tax_year=${taxYear}`)
      ]);
      setInitStatus(statusRes.data);
      setLedgers(ledgerRes.data);
    } catch (error) {
      console.error('YTD fetch error:', error);
      toast.error('Failed to fetch YTD data');
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.post('/payroll-ytd/initialize', {
        ...initForm,
        tax_year: taxYear
      });
      toast.success('YTD initialized successfully');
      setInitDialogOpen(false);
      setInitForm({
        employee_id: '',
        ytd_gross: 0,
        ytd_federal_tax: 0,
        ytd_provincial_tax: 0,
        ytd_cpp: 0,
        ytd_cpp2: 0,
        ytd_ei: 0,
        ytd_net_pay: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize YTD');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !adjustForm.adjustment_reason) {
      toast.error('Please provide an adjustment reason');
      return;
    }
    
    setFormLoading(true);
    try {
      // Only include non-empty values
      const payload = { adjustment_reason: adjustForm.adjustment_reason };
      const fields = ['ytd_gross', 'ytd_federal_tax', 'ytd_provincial_tax', 'ytd_cpp', 'ytd_cpp2', 'ytd_ei', 'ytd_net_pay'];
      fields.forEach(field => {
        if (adjustForm[field] !== '' && adjustForm[field] !== null) {
          payload[field] = parseFloat(adjustForm[field]);
        }
      });
      
      await api.patch(`/payroll-ytd/adjust/${selectedEmployee.employee_id}?tax_year=${taxYear}`, payload);
      toast.success('YTD adjusted successfully');
      setAdjustDialogOpen(false);
      setSelectedEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust YTD');
    } finally {
      setFormLoading(false);
    }
  };

  const openInitDialog = (employee = null) => {
    if (employee) {
      setInitForm(prev => ({ ...prev, employee_id: employee.employee_id }));
    }
    setInitDialogOpen(true);
  };

  const openAdjustDialog = (ledger) => {
    setSelectedEmployee(ledger);
    setAdjustForm({
      ytd_gross: ledger.ytd_gross || '',
      ytd_federal_tax: ledger.ytd_federal_tax || '',
      ytd_provincial_tax: ledger.ytd_provincial_tax || '',
      ytd_cpp: ledger.ytd_cpp || '',
      ytd_cpp2: ledger.ytd_cpp2 || '',
      ytd_ei: ledger.ytd_ei || '',
      ytd_net_pay: ledger.ytd_net_pay || '',
      adjustment_reason: ''
    });
    setAdjustDialogOpen(true);
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(val || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">YTD Payroll Management</h1>
          <p className="text-slate-500">Initialize and manage Year-To-Date payroll values</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2027">2027</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => openInitDialog()} className="bg-teal-600 hover:bg-teal-700">
            <DollarSign className="h-4 w-4 mr-2" />
            Initialize YTD
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      {initStatus && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Initialized</p>
                  <p className="text-2xl font-bold text-emerald-600">{initStatus.initialized_count}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Missing YTD</p>
                  <p className="text-2xl font-bold text-amber-600">{initStatus.not_initialized_count}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Tax Year</p>
                  <p className="text-2xl font-bold text-slate-900">{taxYear}</p>
                </div>
                <Calendar className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Missing Employees Warning */}
      {initStatus?.missing_employees?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Employees Missing YTD Initialization
            </CardTitle>
            <CardDescription className="text-amber-600">
              These employees need YTD values initialized before payroll can be processed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {initStatus.missing_employees.map((emp) => (
                <Badge 
                  key={emp.employee_id} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-amber-100"
                  onClick={() => openInitDialog(emp)}
                >
                  {emp.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Initialized Employees Table */}
      <Card>
        <CardHeader>
          <CardTitle>YTD Ledger</CardTitle>
          <CardDescription>Current Year-To-Date values for all employees</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : ledgers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No YTD records found for {taxYear}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">YTD Gross</TableHead>
                  <TableHead className="text-right">YTD CPP</TableHead>
                  <TableHead className="text-right">YTD EI</TableHead>
                  <TableHead className="text-right">YTD Fed Tax</TableHead>
                  <TableHead className="text-right">YTD Prov Tax</TableHead>
                  <TableHead className="text-right">YTD Net</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgers.map((ledger) => (
                  <TableRow key={ledger.id}>
                    <TableCell className="font-medium">{ledger.employee_name || 'Unknown'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ledger.ytd_gross)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ledger.ytd_cpp)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ledger.ytd_ei)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ledger.ytd_federal_tax)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ledger.ytd_provincial_tax)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(ledger.ytd_net_pay)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openAdjustDialog(ledger)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Initialize Dialog */}
      <Dialog open={initDialogOpen} onOpenChange={setInitDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Initialize YTD Values</DialogTitle>
            <DialogDescription>
              Enter Year-To-Date values from the employee's last paystub before system go-live
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInitialize}>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Employee</Label>
                <Select value={initForm.employee_id} onValueChange={(v) => setInitForm(prev => ({...prev, employee_id: v}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {initStatus?.missing_employees?.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>YTD Gross</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_gross} onChange={(e) => setInitForm(prev => ({...prev, ytd_gross: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <Label>YTD Net Pay</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_net_pay} onChange={(e) => setInitForm(prev => ({...prev, ytd_net_pay: parseFloat(e.target.value) || 0}))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>YTD Federal Tax</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_federal_tax} onChange={(e) => setInitForm(prev => ({...prev, ytd_federal_tax: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <Label>YTD Provincial Tax</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_provincial_tax} onChange={(e) => setInitForm(prev => ({...prev, ytd_provincial_tax: parseFloat(e.target.value) || 0}))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>YTD CPP</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_cpp} onChange={(e) => setInitForm(prev => ({...prev, ytd_cpp: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <Label>YTD CPP2</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_cpp2} onChange={(e) => setInitForm(prev => ({...prev, ytd_cpp2: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <Label>YTD EI</Label>
                  <Input type="number" step="0.01" value={initForm.ytd_ei} onChange={(e) => setInitForm(prev => ({...prev, ytd_ei: parseFloat(e.target.value) || 0}))} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={initForm.notes} onChange={(e) => setInitForm(prev => ({...prev, notes: e.target.value}))} placeholder="Source of YTD values..." />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInitDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !initForm.employee_id} className="bg-teal-600">
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Initialize'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Adjust YTD Values</DialogTitle>
            <DialogDescription>
              Modify Year-To-Date values for {selectedEmployee?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjust}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>YTD Gross</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_gross} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_gross: e.target.value}))} />
                </div>
                <div>
                  <Label>YTD Net Pay</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_net_pay} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_net_pay: e.target.value}))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>YTD Federal Tax</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_federal_tax} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_federal_tax: e.target.value}))} />
                </div>
                <div>
                  <Label>YTD Provincial Tax</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_provincial_tax} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_provincial_tax: e.target.value}))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>YTD CPP</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_cpp} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_cpp: e.target.value}))} />
                </div>
                <div>
                  <Label>YTD CPP2</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_cpp2} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_cpp2: e.target.value}))} />
                </div>
                <div>
                  <Label>YTD EI</Label>
                  <Input type="number" step="0.01" value={adjustForm.ytd_ei} onChange={(e) => setAdjustForm(prev => ({...prev, ytd_ei: e.target.value}))} />
                </div>
              </div>
              <div>
                <Label className="text-red-600">Adjustment Reason (Required)</Label>
                <Textarea value={adjustForm.adjustment_reason} onChange={(e) => setAdjustForm(prev => ({...prev, adjustment_reason: e.target.value}))} placeholder="Reason for adjustment..." required />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !adjustForm.adjustment_reason} className="bg-amber-600">
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
